import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

export default function AdminPage() {
  // The three brands we support
  const brands = ["Apple", "Samsung", "Google"];

  // All providers
  const allProviders = ["Rogers", "Bell", "Telus", "Fido", "Virgin", "Koodo", "Freedom"];

  // Providers that do NOT offer return
  const noReturnProviders = ["Fido", "Virgin", "Koodo"];

  // phonesData structure in Firestore:
  // {
  //   "Apple": {
  //     "iPhone 16": {
  //       providers: {
  //         Rogers: { financing: 20, returnOption: 10 },
  //         Fido:   { financing: 18 },  // no returnOption
  //         ...
  //       }
  //     },
  //     ...
  //   },
  //   "Samsung": { ... },
  //   "Google":  { ... }
  // }
  const [phonesData, setPhonesData] = useState({});

  // For adding a new phone
  const [selectedBrandForNewPhone, setSelectedBrandForNewPhone] = useState("Apple");
  const [newPhoneName, setNewPhoneName] = useState("");

  // -----------------------------
  // LOAD from Firestore on mount
  // -----------------------------
  useEffect(() => {
    const fetchPhonesDoc = async () => {
      try {
        const ref = doc(db, "pricing", "phones");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setPhonesData(snap.data());
        } else {
          // If no doc yet, set empty structure
          setPhonesData({});
        }
      } catch (error) {
        console.error("Error fetching phones doc:", error);
        alert("Failed to load phone data from Firestore.");
      }
    };
    fetchPhonesDoc();
  }, []);

  // -----------------------------
  // UTILITY: Check if a provider has Return or not
  // -----------------------------
  const hasReturn = (provider) => !noReturnProviders.includes(provider);

  // -----------------------------
  // EDIT existing phone's provider data
  // -----------------------------
  const handleChange = (brand, phoneName, provider, key, value) => {
    setPhonesData((prev) => {
      const updated = { ...prev };
      if (!updated[brand]) {
        updated[brand] = {};
      }
      if (!updated[brand][phoneName]) {
        updated[brand][phoneName] = { providers: {} };
      }
      if (!updated[brand][phoneName].providers[provider]) {
        // For a no-return provider, only store financing
        if (hasReturn(provider)) {
          updated[brand][phoneName].providers[provider] = { financing: 0, returnOption: 0 };
        } else {
          updated[brand][phoneName].providers[provider] = { financing: 0 };
        }
      }

      // Parse numeric input
      const numericVal = parseFloat(value) || 0;

      // If this provider has return, we can set "financing" or "returnOption"
      // If it does NOT have return, we only store "financing"
      if (key === "returnOption" && !hasReturn(provider)) {
        // Do nothing if provider doesn't support return
      } else {
        updated[brand][phoneName].providers[provider][key] = numericVal;
      }
      return updated;
    });
  };

  // -----------------------------
  // ADD a new phone under a brand
  // -----------------------------
  const addPhone = () => {
    const brand = selectedBrandForNewPhone;
    const phoneName = newPhoneName.trim();
    if (!phoneName) {
      alert("Please enter a phone name.");
      return;
    }

    setPhonesData((prev) => {
      const updated = { ...prev };
      if (!updated[brand]) {
        updated[brand] = {};
      }
      if (updated[brand][phoneName]) {
        alert("That phone already exists under this brand.");
        return updated; // no change
      }
      // Create default providers data
      const newPhoneObj = { providers: {} };
      allProviders.forEach((prov) => {
        if (hasReturn(prov)) {
          newPhoneObj.providers[prov] = { financing: 0, returnOption: 0 };
        } else {
          newPhoneObj.providers[prov] = { financing: 0 };
        }
      });
      updated[brand][phoneName] = newPhoneObj;
      return updated;
    });

    setNewPhoneName("");
  };

  // -----------------------------
  // DELETE phone
  // -----------------------------
  const deletePhone = (brand, phoneName) => {
    if (!window.confirm(`Delete ${phoneName} under ${brand}?`)) return;
    setPhonesData((prev) => {
      const updated = { ...prev };
      if (!updated[brand]) return updated;
      delete updated[brand][phoneName];
      return updated;
    });
  };

  // -----------------------------
  // SAVE to Firestore
  // -----------------------------
  const saveAll = async () => {
    try {
      await setDoc(doc(db, "pricing", "phones"), phonesData);
      alert("Phone data saved to Firestore!");
    } catch (error) {
      console.error("Error saving phone data:", error);
      alert("Failed to save: " + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 flex flex-col items-center">
      <h1 className="text-3xl font-bold mb-6">Admin Panel</h1>

      {["Apple", "Samsung", "Google"].map((brand) => {
        const brandPhones = phonesData[brand] || {};

        return (
          <div key={brand} className="bg-white p-6 rounded shadow-md max-w-6xl w-full mb-6">
            <h2 className="text-2xl font-semibold mb-4">{brand}</h2>
            <div className="overflow-x-auto">
              <table className="min-w-max border border-gray-300 text-center">
                <thead className="bg-black text-white">
                  <tr>
                    <th className="border px-4 py-2">Phone Name</th>
                    {/* For each provider, if it supports return, we show 2 columns (Financing, Return).
                        If not, we show 1 column (Financing). */}
                    {allProviders.map((prov) => (
                      <th
                        key={prov}
                        colSpan={hasReturn(prov) ? 2 : 1}
                        className="border px-4 py-2"
                      >
                        {prov}
                      </th>
                    ))}
                    <th className="border px-4 py-2">Delete</th>
                  </tr>
                  <tr className="bg-gray-700 text-white">
                    <th className="border px-4 py-2"></th>
                    {allProviders.map((prov) => {
                      if (hasReturn(prov)) {
                        return (
                          <React.Fragment key={prov}>
                            <th className="border px-4 py-2">Financing</th>
                            <th className="border px-4 py-2">Return</th>
                          </React.Fragment>
                        );
                      } else {
                        return (
                          <th key={prov} className="border px-4 py-2">
                            Financing
                          </th>
                        );
                      }
                    })}
                    <th className="border px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(brandPhones).map(([phoneName, phoneObj]) => (
                    <tr key={phoneName} className="bg-gray-50">
                      <td className="border px-4 py-2 font-bold">{phoneName}</td>
                      {allProviders.map((prov) => {
                        // If provider object doesn't exist, default to {financing,returnOption}
                        const pData = phoneObj.providers?.[prov] || {};
                        // Financing
                        const financingVal = pData.financing || 0;
                        // Return, only if hasReturn(prov)
                        const returnVal = hasReturn(prov) ? (pData.returnOption || 0) : 0;

                        if (hasReturn(prov)) {
                          return (
                            <React.Fragment key={prov}>
                              {/* Financing cell */}
                              <td className="border px-4 py-2">
                                <input
                                  type="number"
                                  className="w-20 p-1 border rounded"
                                  value={financingVal}
                                  onChange={(e) =>
                                    handleChange(
                                      brand,
                                      phoneName,
                                      prov,
                                      "financing",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              {/* Return cell */}
                              <td className="border px-4 py-2">
                                <input
                                  type="number"
                                  className="w-20 p-1 border rounded"
                                  value={returnVal}
                                  onChange={(e) =>
                                    handleChange(
                                      brand,
                                      phoneName,
                                      prov,
                                      "returnOption",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                            </React.Fragment>
                          );
                        } else {
                          // No-return provider -> single financing cell
                          return (
                            <td key={prov} className="border px-4 py-2">
                              <input
                                type="number"
                                className="w-20 p-1 border rounded"
                                value={financingVal}
                                onChange={(e) =>
                                  handleChange(brand, phoneName, prov, "financing", e.target.value)
                                }
                              />
                            </td>
                          );
                        }
                      })}
                      {/* Delete phone */}
                      <td className="border px-4 py-2">
                        <button
                          onClick={() => deletePhone(brand, phoneName)}
                          className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* ADD NEW PHONE */}
      <div className="bg-white p-6 rounded shadow-md max-w-6xl w-full mb-6">
        <h3 className="text-xl font-semibold mb-4">Add New Phone</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block mb-1 text-gray-700">Brand</label>
            <select
              className="border p-2 rounded w-full"
              value={selectedBrandForNewPhone}
              onChange={(e) => setSelectedBrandForNewPhone(e.target.value)}
            >
              {brands.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block mb-1 text-gray-700">Phone Name</label>
            <input
              type="text"
              className="border p-2 rounded w-full"
              placeholder="e.g. iPhone 16 Pro"
              value={newPhoneName}
              onChange={(e) => setNewPhoneName(e.target.value)}
            />
          </div>
          <div className="flex-1 flex items-end">
            <button
              onClick={addPhone}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 w-full"
            >
              Add Phone
            </button>
          </div>
        </div>
      </div>

      {/* SAVE BUTTON */}
      <button
        onClick={saveAll}
        className="bg-black text-white px-6 py-3 rounded hover:bg-gray-800"
      >
        Save All
      </button>
    </div>
  );
}
